import type { FormEvent } from "react";
import { FaFilter, FaSearch, FaTimes } from "react-icons/fa";
import { SlotRequestsFilter } from "../slot-requests.service";

type Props = {
  initialQuery: SlotRequestsFilter;
  updateQuery: (filters: SlotRequestsFilter) => void;
};

const getInitialSearch = (query: SlotRequestsFilter) => {
  if (query.requestId) return { field: "requestId", value: query.requestId };
  if (query.learnerId) return { field: "learnerId", value: query.learnerId };
  if (query.coachId) return { field: "coachId", value: query.coachId };
  return { field: "requestId", value: "" };
};

export const SlotRequestsFilters = ({ initialQuery, updateQuery }: Props) => {
  const initialSearch = getInitialSearch(initialQuery);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const searchField = formData.get("searchField") as "requestId" | "learnerId" | "coachId";
    const searchValue = String(formData.get("searchValue") || "").trim();
    const filters: SlotRequestsFilter = {};

    if (searchValue) filters[searchField] = searchValue;
    updateQuery(filters);
  };

  return (
    <form
      onSubmit={handleSubmit}
      onReset={(event) => {
        event.preventDefault();
        updateQuery({});
      }}
      className="mb-5 border border-slate-200 bg-white shadow-sm"
    >
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-700">
        <FaFilter className="text-emerald-600" />
        Filter requests
      </div>

      <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-[170px_minmax(0,1fr)_auto]">
        <select
          name="searchField"
          defaultValue={initialSearch.field}
          aria-label="Search field"
          className="h-10 border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none transition-colors focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
        >
          <option value="requestId">Request ID</option>
          <option value="learnerId">Learner ID</option>
          <option value="coachId">Coach ID</option>
        </select>

        <input
          name="searchValue"
          type="search"
          defaultValue={initialSearch.value}
          placeholder="Enter an ID"
          className="h-10 min-w-0 border border-slate-300 px-3 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
        />

        <div className="flex gap-2">
          <button
            type="submit"
            className="inline-flex h-10 items-center justify-center gap-2 bg-emerald-600 px-4 text-sm font-medium text-white transition-colors hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2"
          >
            <FaSearch />
            Apply
          </button>
          <button
            type="reset"
            title="Clear filters"
            aria-label="Clear filters"
            className="inline-flex h-10 w-10 items-center justify-center border border-slate-300 text-slate-600 transition-colors hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-200"
          >
            <FaTimes />
          </button>
        </div>
      </div>
    </form>
  );
};
